import { useId } from "react";
import { cn } from "@/lib/utils";
import { clampToRange } from "./indicatorMath";

const TRACK_COLOR = "#EEE9E1";
const DAYS_LEFT_GRADIENT =
  "linear-gradient(to right, #B42E77 0%, #D14E2F 38%, #F1D432 70%, #23794A 100%)";

function toTime(value: string | Date): number {
  return value instanceof Date ? value.getTime() : new Date(value).getTime();
}

/**
 * Pure computation shared by the component and its tests: how far the month
 * has elapsed (and how many days are left), plus the clamped goal
 * percentage. Dates outside the [monthStart, monthEnd] window clamp to 0%/
 * 100% elapsed instead of extrapolating past the track.
 */
export function computeMonthBarProgress(
  currentDate: string | Date,
  monthStart: string | Date,
  monthEnd: string | Date,
  goalPercent: number,
) {
  const startTime = toTime(monthStart);
  const endTime = toTime(monthEnd);
  const currentTime = toTime(currentDate);
  const totalSpan = endTime - startTime;
  const rawProgressPercent =
    totalSpan > 0 ? ((currentTime - startTime) / totalSpan) * 100 : 100;
  const monthProgressPercent = clampToRange(rawProgressPercent, 0, 100);
  return {
    monthProgressPercent,
    daysLeftPercent: 100 - monthProgressPercent,
    goalPercent: clampToRange(goalPercent, 0, 100),
  };
}

export interface MonthBarProps {
  /** Current date used to compute how far the month has progressed. */
  currentDate: string | Date;
  /** First day of the month window. */
  monthStart: string | Date;
  /** Last day of the month window. */
  monthEnd: string | Date;
  /** Percentage of the goal achieved so far; clamped to [0, 100]. */
  goalPercent: number;
  /** Visible label under the track's start (e.g. the month's first day). */
  startLabel: string;
  /** Visible label under the track's end (e.g. the month's last day). */
  endLabel: string;
  /** Visible summary sentence (e.g. "12 days left, 64% of goal"); also the accessible name. */
  summaryLabel: string;
  /**
   * Whether the ▲ goal marker renders at all (TASK-092, "Mi avance"): the
   * marker's position needs a units goal to mean anything, so a caller with
   * no such goal hides it entirely instead of drawing it at a meaningless
   * 0%. Defaults to `true` (every pre-existing caller keeps the marker).
   */
  showGoalMarker?: boolean;
  className?: string;
}

/**
 * Full-width days-left bar for the Direccion panel and the advisor's "Mi
 * avance" screen: a gradient tail anchored at the end of the track that
 * shrinks as the month passes, plus a ▲ marker at the percentage of goal
 * achieved. The caller-supplied summary is rendered as visible text (not
 * only an aria-label, which would never appear on screen) and doubles as
 * the container's accessible name via aria-labelledby.
 */
export function MonthBar({
  currentDate,
  monthStart,
  monthEnd,
  goalPercent,
  startLabel,
  endLabel,
  summaryLabel,
  showGoalMarker = true,
  className,
}: MonthBarProps) {
  const reactId = useId();
  const summaryId = `${reactId}-month-bar-summary`;

  const { monthProgressPercent, goalPercent: clampedGoalPercent } =
    computeMonthBarProgress(currentDate, monthStart, monthEnd, goalPercent);

  return (
    <div
      role="img"
      aria-labelledby={summaryId}
      data-testid="month-bar"
      className={cn("w-full", className)}
    >
      <div
        className="relative h-2 w-full overflow-hidden rounded-full"
        style={{ backgroundColor: TRACK_COLOR }}
      >
        <div
          aria-hidden="true"
          data-testid="month-bar-gradient"
          className="absolute inset-0"
          style={{ backgroundImage: DAYS_LEFT_GRADIENT }}
        />
        <div
          aria-hidden="true"
          data-testid="month-bar-elapsed-cover"
          className="absolute inset-y-0 left-0"
          style={{
            width: `${monthProgressPercent}%`,
            backgroundColor: TRACK_COLOR,
          }}
        />
        {showGoalMarker ? (
          <div
            aria-hidden="true"
            data-testid="month-bar-goal-marker"
            className="absolute -top-1 -translate-x-1/2 text-[12.5px] leading-none"
            style={{ left: `${clampedGoalPercent}%`, color: "#272C57" }}
          >
            ▲
          </div>
        ) : null}
      </div>
      <div className="mt-1 flex items-center justify-between text-[12.5px] tabular-nums">
        <span data-testid="month-bar-start-label">{startLabel}</span>
        <span data-testid="month-bar-end-label">{endLabel}</span>
      </div>
      <p
        id={summaryId}
        data-testid="month-bar-summary"
        className="mt-1 text-[12.5px] tabular-nums"
      >
        {summaryLabel}
      </p>
    </div>
  );
}
